import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };

const isSystem = (by, note) => {
  const b = String(by || '').toUpperCase();
  if (!b || b.startsWith('ARIA') || b.startsWith('AIRA') || b.startsWith('SYSTEM') || b.startsWith('PEN') || b === 'AUTONOMY') return true;
  if (note && ['system', 'nudge', 'reconciliation', 'sweep'].includes(note.type)) return true;
  const t = String(note?.text || '').toLowerCase();
  return ['sweep)', 'legacy sweep', 'hygiene', 'reconciliation:', 'backfill'].some(k => t.includes(k));
};

// Mirrors backend claims_nudge.last_worked: the work clock, else the latest human note, else FNOL — ARIA's writes never count
export const idleDays = (claim) => {
  let ref = claim?.last_worked_at;
  if (!ref) {
    const human = (claim?.notes || []).filter(n => !isSystem(n.by, n) && n.at).map(n => n.at).sort();
    ref = human.length ? human[human.length - 1] : claim?.created_at;
  }
  if (!ref) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(ref).getTime()) / 86400000));
};

export function IdleChip({ claim }) {
  const d = idleDays(claim);
  if (d < 60 || ['settled', 'closed', 'closed_no_payment', 'declined', 'withdrawn'].includes(claim.status)) return null;
  const lvl = claim.stale_nudge?.level;
  const color = d >= 90 ? C.red : lvl === 'escalate' ? C.red : C.amber;
  const label = d >= 90 ? `IDLE ${d}d · IN-26` : lvl === 'escalate' ? `IDLE ${d}d · escalated` : lvl === 'chase' ? `IDLE ${d}d · chased` : `IDLE ${d}d`;
  return <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: color + '18', color }} title="Days since the last handler action; ARIA chases at 60, escalates at 74, IN-26 breaches at 90" data-testid={`claim-idle-chip-${claim.claim_id}`}>{label}</span>;
}

export default function StaleClaimsCard({ api, onOpen }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get('/claims/stale').then(r => setData(r.data)).catch(() => setData(null)), [api]);
  useEffect(() => { load(); }, [load]);

  const sweep = async () => {
    setBusy(true);
    try {
      const r = await api.post('/claims/stale/sweep');
      toast.success(`ARIA chased ${r.data.chased} handler(s), escalated ${r.data.escalated}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Chase failed'); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const n = data.count;
  const color = n ? C.amber : C.green;
  const nextUp = (data.claims || []).filter(c => c.next === 'chase' || c.next === 'escalate').length;
  const escalated = (data.claims || []).filter(c => c.stale_nudge?.level === 'escalate').length;
  return (
    <Card className="rounded-md" style={{ border: `1px solid ${color}4D` }} data-testid="stale-claims-card">
      <div className="w-full flex items-center justify-between px-4 py-3">
        <button onClick={() => setOpen(v => !v)} className="flex items-center gap-2 flex-wrap text-left" data-testid="stale-claims-toggle">
          <Clock className="w-4 h-4" style={{ color }} />
          <span className="text-sm font-semibold" style={{ color: C.navy }}>ARIA Claims — stale files</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: color + '18', color }} data-testid="stale-claims-count">{n} idle {data.thresholds.chase}+ days</span>
          <span className="text-[11px] hidden md:inline" style={{ color: C.slate }}>chase at {data.thresholds.chase}d · escalate at {data.thresholds.escalate}d · IN-26 at {data.thresholds.invariant}d</span>
        </button>
        <div className="flex items-center gap-2">
          {escalated > 0 && <a href="/claims?tab=reassign" className="text-[11px] px-2 py-1 rounded-sm font-semibold" style={{ background: C.red + '18', color: C.red }} data-testid="stale-claims-reassign-link">Reassign sweep · {escalated} escalated →</a>}
          <button onClick={sweep} disabled={busy || !nextUp} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm text-white disabled:opacity-50" style={{ background: C.blue }} title={nextUp ? `${nextUp} file(s) due a chase or escalation` : 'Nothing due — every idle file was chased inside 14 days'} data-testid="stale-claims-chase-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Chase now{nextUp ? ` (${nextUp})` : ''}
          </button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3">
          {n === 0 ? <p className="text-[11px]" style={{ color: C.slate }}>Every open file has had a handler action inside {data.thresholds.chase} days.</p> : (
            <table className="w-full text-[11px]" data-testid="stale-claims-table">
              <thead><tr style={{ color: C.slate }}><th className="text-left py-1">claim</th><th className="text-left">insured</th><th className="text-left">handler</th><th className="text-left">status</th><th className="text-right">idle</th><th className="text-right">to IN-26</th><th className="text-left pl-3">ARIA</th></tr></thead>
              <tbody>
                {data.claims.slice(0, 25).map(c => (
                  <tr key={c.claim_id} className="border-t cursor-pointer hover:bg-[#FAFBFC]" style={{ borderColor: 'rgba(136,150,168,0.15)' }} onClick={() => onOpen && onOpen(c.claim_id)} data-testid={`stale-claim-row-${c.claim_id}`}>
                    <td className="py-1 font-mono" style={{ color: C.blue }}>{c.claim_reference || c.claim_number}</td>
                    <td className="py-1 truncate max-w-[180px]" style={{ color: C.navy }}>{c.business_name}</td>
                    <td className="py-1" style={{ color: C.slate }}>{c.handler_name || '—'}</td>
                    <td className="py-1 capitalize" style={{ color: C.slate }}>{(c.status || '').replace(/_/g, ' ')}</td>
                    <td className="py-1 text-right font-semibold" style={{ color: c.idle_days >= 90 ? C.red : C.amber }}>{c.idle_days}d</td>
                    <td className="py-1 text-right" style={{ color: c.days_to_invariant <= 7 ? C.red : C.slate }}>{c.days_to_invariant}d</td>
                    <td className="py-1 pl-3" style={{ color: C.slate }}>{c.stale_nudge ? `${c.stale_nudge.level} ×${c.stale_nudge.count} · ${(c.stale_nudge.last_at || '').slice(0, 10)}` : c.next}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="text-[10px] mt-2" style={{ color: C.slate }}>{data.basis}{data.config?.test_mode ? ' · digest goes to the safe inbox while test mode is on' : ''}</p>
        </div>
      )}
    </Card>
  );
}
